import React, { useEffect, useState } from 'react';
import { reachConfigAPI, ReachEngagementConfig } from '../../../services/reachService';
import { WeekSelector } from '../common/WeekSelector';
import { PulseTab } from './PulseTab';
import { TrackerTab } from './TrackerTab';
import { PlaybookTab } from './PlaybookTab';
import { ConfigGate } from '../common/ConfigGate';

interface Props {
  projectId: string;
}

const TABS = ['Pulse', 'Tracker', 'Playbook'] as const;
type Tab = typeof TABS[number];

const STAGE_COLORS: Record<string, string> = {
  R: '#f59e0b',
  E: '#3b82f6',
  A: '#8b5cf6',
  C: '#10b981',
  H: '#ec4899',
};

// Duration → week list mirrors durationMapping in reach-playbook-defaults.json
const DURATION_WEEKS: Record<number, number[]> = {
  6: [1, 2, 3, 5, 7, 9],
  8: [1, 2, 3, 4, 5, 6, 7, 8],
  12: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
};

const WEEK_STAGES: Record<number, string> = {
  1: 'R', 2: 'R', 3: 'E', 4: 'E', 5: 'A', 6: 'A',
  7: 'C', 8: 'C', 9: 'H', 10: 'H', 11: 'H', 12: 'H',
};

export const WorkbookShell: React.FC<Props> = ({ projectId }) => {
  const [config, setConfig] = useState<ReachEngagementConfig | null | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<Tab>('Pulse');
  const [selectedWeek, setSelectedWeek] = useState(1);

  useEffect(() => {
    reachConfigAPI.get(projectId).then(r => setConfig(r.data.config ?? null));
  }, [projectId]);

  if (config === undefined) return null;

  if (!config) return (
    <ConfigGate hasConfig={false}><></></ConfigGate>
  );

  const duration = config.engagementDurationWeeks ?? 8;
  const weeks = DURATION_WEEKS[duration] ?? DURATION_WEEKS[8];
  const stageLabels = Object.fromEntries(weeks.map(w => [w, WEEK_STAGES[w] ?? '']));
  const stageColors = Object.fromEntries(weeks.map(w => [w, STAGE_COLORS[WEEK_STAGES[w] ?? 'R']]));

  return (
    <div className="flex flex-col gap-5">
      {/* Week selector */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
        <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">Select Week</p>
        <WeekSelector
          weeks={weeks}
          selected={selectedWeek}
          onChange={setSelectedWeek}
          stageColors={stageColors}
          stageLabels={stageLabels}
        />
      </div>

      {/* Tab bar */}
      <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden bg-white">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2.5 text-sm font-semibold transition-all ${
              activeTab === tab ? 'bg-primary text-white' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'Pulse' && (
        <PulseTab projectId={projectId} week={selectedWeek} />
      )}
      {activeTab === 'Tracker' && (
        <TrackerTab projectId={projectId} week={selectedWeek} allWeeks={weeks} />
      )}
      {activeTab === 'Playbook' && (
        <PlaybookTab projectId={projectId} week={selectedWeek} />
      )}
    </div>
  );
};
